import { useCallback, useEffect, useState } from "react";
import type { TagSuggestion } from "@gooner/shared";
import { adminApi } from "../lib/api";
import { isSafeMode } from "../lib/safe-mode";
import { Button } from "../control/ui/primitives";

/**
 * Review queue for user-submitted tag suggestions. Each suggestion pairs a clip
 * with a proposed tag; accepting applies it to the clip's global tags, rejecting
 * discards it. Both actions drop the row on success.
 */
export function TagSuggestions() {
  const [suggestions, setSuggestions] = useState<TagSuggestion[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { suggestions: list } = await adminApi.listTagSuggestions();
      setSuggestions(list);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function act(id: number, kind: "accept" | "reject") {
    setBusy(id);
    try {
      if (kind === "accept") await adminApi.acceptTagSuggestion(id);
      else await adminApi.rejectTagSuggestion(id);
      setSuggestions((prev) => prev.filter((s) => s.id !== id));
    } catch {
      // Likely already handled (404) — refresh to resync.
      void load();
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-lg font-semibold text-zinc-100">Tag suggestions</h1>
          <p className="text-xs text-zinc-500">
            User-submitted tags awaiting review. Accepting applies the tag to the clip.
          </p>
        </div>
        <span className="rounded-full bg-[var(--color-panel-2)] px-2.5 py-1 text-xs font-medium text-zinc-300">
          {suggestions.length} pending
        </span>
      </header>

      {loading ? (
        <p className="text-sm text-zinc-500">Loading…</p>
      ) : suggestions.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-edge)] p-8 text-center text-sm text-zinc-500">
          No pending tag suggestions.
        </div>
      ) : (
        <ul className="space-y-2">
          {suggestions.map((s) => (
            <li
              key={s.id}
              className="flex items-center gap-3 rounded-xl border border-[var(--color-edge)] bg-[var(--color-panel)] p-2.5"
            >
              <Thumb suggestion={s} />
              <div className="min-w-0 flex-1">
                <div className="truncate text-sm font-medium text-zinc-100">#{s.tag}</div>
                <div className="truncate text-xs text-zinc-500">
                  {s.suggestedBy ? `by ${s.suggestedBy}` : "by anonymous"}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                <Button size="sm" onClick={() => void act(s.id, "accept")} disabled={busy === s.id}>
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => void act(s.id, "reject")}
                  disabled={busy === s.id}
                >
                  Reject
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Small clip preview: poster image when present, else a muted looping video. */
function Thumb({ suggestion }: { suggestion: TagSuggestion }) {
  const { clip } = suggestion;
  const cls = "h-14 w-14 shrink-0 rounded-lg border border-[var(--color-edge)] bg-black object-cover";
  if (clip.posterUrl) {
    return <img src={clip.posterUrl} alt="" className={cls} />;
  }
  return (
    <video
      src={clip.url}
      className={cls}
      muted
      loop
      playsInline
      preload="metadata"
      disablePictureInPicture={isSafeMode()}
    />
  );
}
